import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, ChevronUp, Edit2,
  Calendar, History, Package, Search,
  ChevronLeft, ChevronRight, User,
  AlertTriangle, CheckCircle, Calculator,
} from 'lucide-react';

import { normalizeLoyalty } from '../../../domain/loyaltyCalculations';
import { TierBadge, LoyaltyHistoryList } from '../../loyalty';
import CustomDeleteDialog from '../../../components/CustomDeleteDialog';

import PackagesPanel from './PackagesPanel';
import useCustomersStore from '../store/customersStore';
import { useCustomerBills } from '../hooks/useCustomerBills';

// ── Util ──────────────────────────────────────────────────────────────────────

const formatDate = (dateStr, locale = 'tr-TR') => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const BILLS_PER_PAGE = 6;

// ── ExpandableDetail ──────────────────────────────────────────────────────────
// Veriyi prop olarak alır — CustomerRow seviyesinde yaşıyor (expand/collapse korunuyor)

const ExpandableDetail = React.memo(({
  customer,
  employees,
  onBackfill,
  backfillLoading,
  bills,
  trackingMap,
  packages,
  loading,
}) => {
  const { t, i18n } = useTranslation('customers');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-GB';

  const [activeTab, setActiveTab] = useState('bills');

  const billSearchTerms    = useCustomersStore(s => s.billSearchTerms);
  const billCurrentPages   = useCustomersStore(s => s.billCurrentPages);
  const setBillSearchTerm  = useCustomersStore(s => s.setBillSearchTerm);
  const setBillCurrentPage = useCustomersStore(s => s.setBillCurrentPage);

  const searchTerm  = billSearchTerms[customer.id] || '';
  const currentPage = billCurrentPages[customer.id] || 1;

  const filteredBills = bills.filter(b => {
    if (!searchTerm) return true;
    return (b.services?.map(s => s.name).join(' ') || '').toLowerCase().includes(searchTerm.toLowerCase());
  });
  const totalPages = Math.ceil(filteredBills.length / BILLS_PER_PAGE);
  const pagedBills = filteredBills.slice((currentPage - 1) * BILLS_PER_PAGE, currentPage * BILLS_PER_PAGE);

  const loyalty        = normalizeLoyalty(customer.loyalty);
  const activePackages = packages.filter(p => p.status === 'active').length;

  const TABS = [
    { key: 'bills',    icon: Calendar, labelKey: 'tabs.bills',    badge: bills.length || null },
    { key: 'loyalty',  icon: History,  labelKey: 'tabs.loyalty',  badge: null },
    { key: 'packages', icon: Package,  labelKey: 'tabs.packages', badge: activePackages || null, badgeCls: 'bg-blue-100 text-blue-600' },
  ];

  return (
    <tr>
      <td colSpan="6" className="px-6 py-4 bg-gray-50">
        <div className="space-y-4">

          {/* Tab bar */}
          <div className="flex gap-1 border-b border-gray-200">
            {TABS.map(({ key, icon: Icon, labelKey, badge, badgeCls = 'bg-gray-100 text-gray-600' }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(labelKey)}
                {badge ? (
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${badgeCls}`}>{badge}</span>
                ) : null}
              </button>
            ))}
          </div>

          {/* ── Loyalty ── */}
          {activeTab === 'loyalty' && (
            <div className="space-y-4">
              {loyalty.backfillCompletedAt ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2.5">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <span className="font-medium text-green-900">{t('backfill.done')}</span>
                    {loyalty.backfillBillCount != null && (
                      <span className="text-green-700 ml-2">{t('backfill.doneCount', { count: loyalty.backfillBillCount })}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-start gap-2.5 flex-1">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-amber-900">{t('backfill.title')}</h4>
                      <p className="text-xs text-amber-800 mt-0.5">
                        {t('backfill.description')}{' '}<strong>{t('backfill.warning')}</strong>
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onBackfill(customer)}
                    disabled={backfillLoading === customer.id}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-md hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {backfillLoading === customer.id ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{t('backfill.processing')}</>
                    ) : (
                      <><Calculator className="h-4 w-4" />{t('backfill.calculate')}</>
                    )}
                  </button>
                </div>
              )}
              <LoyaltyHistoryList customerId={customer.id} />
            </div>
          )}

          {/* ── Packages ── */}
          {activeTab === 'packages' && <PackagesPanel packages={packages} />}

          {/* ── Bills ── */}
          {activeTab === 'bills' && loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              <span className="ml-3 text-gray-600">{t('bills.loading')}</span>
            </div>
          )}

          {activeTab === 'bills' && !loading && bills.length === 0 && (
            <p className="text-center text-gray-500 py-8">{t('bills.empty')}</p>
          )}

          {activeTab === 'bills' && !loading && bills.length > 0 && (
            <>
              <div className="flex flex-col md:flex-row justify-between items-center gap-3">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder={t('search.billPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setBillSearchTerm(customer.id, e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t('pagination.page', { page: currentPage, total: totalPages || 1 })}
                </p>
              </div>

              {pagedBills.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('bills.noResults')}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pagedBills.map((bill) => {
                    const tracking  = trackingMap[bill.id];
                    const employee  = employees.find(e => e.id === bill.employeeId);
                    const empName   = employee ? `${employee.name} ${employee.surname}` : t('bills.unknown');
                    const svcNames  = bill.services?.map(s =>
                      `${s.name}${s.sessions > 1 ? ` (${s.sessions} ${t('bills.sessions')})` : ''}`
                    ).join(', ') || t('bills.unknown');

                    let grouped = {};
                    if (tracking?.dependencies) {
                      if (Array.isArray(tracking.dependencies)) {
                        grouped[t('bills.generalGroup')] = tracking.dependencies;
                      } else {
                        Object.entries(tracking.dependencies).forEach(([svcId, svcData]) => {
                          if (svcData.dependencies) {
                            const svc = bill.services?.find(s => s.id === svcId);
                            grouped[svc?.name || t('bills.unknown')] = Object.values(svcData.dependencies).map(d => ({
                              name: d.name, contentValue: d.contentValue, description: d.description || '',
                            }));
                          }
                        });
                      }
                    }
                    const hasTracking = Object.keys(grouped).length > 0;

                    return (
                      <div key={bill.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-lg transition-all">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{formatDate(bill.date, dateLocale)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs">
                              <User className="h-3.5 w-3.5 text-blue-500" />
                              <span className="text-blue-600 font-medium">{empName}</span>
                            </div>
                          </div>
                          <div className="px-3 py-1 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-full">
                            <span className="text-sm font-bold text-blue-600">{bill.totalPrice || 0} TL</span>
                          </div>
                        </div>
                        <h4 className="text-base font-semibold text-gray-900 mb-3 line-clamp-2">{svcNames}</h4>
                        {hasTracking && (
                          <div className="space-y-2">
                            {Object.entries(grouped).map(([svcName, deps]) => (
                              <div key={svcName} className="space-y-1">
                                <p className="text-xs font-semibold text-blue-600">{svcName}</p>
                                <div className="space-y-0.5 pl-2">
                                  {deps.map((dep, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs">
                                      <div className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0 mt-1.5" />
                                      <span className="text-gray-600 flex-1">
                                        <span className="font-medium text-gray-700">{dep.name}:</span>{' '}
                                        {dep.contentValue}
                                        {dep.description && <span className="text-gray-500 italic"> - {dep.description}</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {bill.note && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold text-gray-700">{t('bills.note')}:</span>{' '}
                              <span className="italic whitespace-pre-wrap">{bill.note}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <button
                    onClick={() => setBillCurrentPage(customer.id, Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-100"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setBillCurrentPage(customer.id, p)}
                      className={`px-3 py-1 text-sm border rounded-md ${p === currentPage ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}`}
                    >{p}</button>
                  ))}
                  <button
                    onClick={() => setBillCurrentPage(customer.id, Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-100"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
});

// ── CustomerRow ───────────────────────────────────────────────────────────────

const CustomerRow = React.memo(({ customer, employees, isAdmin, onBackfill, onDelete, onExpand }) => {
  const { t, i18n } = useTranslation('customers');
  const numLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  const isExpanded      = useCustomersStore(s => s.expandedCustomerId === customer.id);
  const toggleExpand    = useCustomersStore(s => s.toggleExpand);
  const openModal       = useCustomersStore(s => s.openModal);
  const backfillLoading = useCustomersStore(s => s.backfillLoading);

  // Veri CustomerRow seviyesinde yaşar → expand/collapse'da korunur
  const { bills, trackingMap, packages, loading } = useCustomerBills(customer.id, isExpanded);

  // Expiry check — satır açıldığında arka planda tetiklenir
  useEffect(() => {
    if (isExpanded && onExpand) onExpand(customer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const loyalty = normalizeLoyalty(customer.loyalty);

  return (
    <React.Fragment>
      <tr className={`transition-all duration-200 ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
        <td
          className="px-6 py-4 whitespace-nowrap cursor-pointer"
          onClick={() => toggleExpand(customer.id)}
        >
          <div className="flex items-center gap-2">
            {isExpanded
              ? <ChevronUp className="h-4 w-4 text-blue-500" />
              : <ChevronDown className="h-4 w-4 text-gray-400" />}
            <span className="font-medium">{customer.name}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">{customer.surname}</td>
        <td className="px-6 py-4 whitespace-nowrap">{customer.emailAddress}</td>
        <td className="px-6 py-4 whitespace-nowrap">
          {customer.phone?.countryCode} {customer.phone?.number}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {loyalty.currentPoints.toLocaleString(numLocale)}
            </span>
            <TierBadge
              tier={loyalty.tier}
              size="sm"
              tooltip={t('loyalty.lifetimeTooltip', { points: loyalty.lifetimePoints.toLocaleString(numLocale) })}
            />
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); openModal(customer); }}
            className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            {t('actions.edit')}
          </button>
          <CustomDeleteDialog
            onConfirm={() => onDelete(customer.id)}
            entityType="musteri"
          />
        </td>
      </tr>

      {isExpanded && (
        <ExpandableDetail
          customer={customer}
          employees={employees}
          isAdmin={isAdmin}
          onBackfill={onBackfill}
          backfillLoading={backfillLoading}
          bills={bills}
          trackingMap={trackingMap}
          packages={packages}
          loading={loading}
        />
      )}
    </React.Fragment>
  );
});

export default CustomerRow;
